import React, { memo, useContext, useMemo } from 'react';
import { TouchableHighlight, View } from 'react-native';
import { CardView, rem, SettingsItem } from 'design-system';
import Amount from 'design-system/Amount';
import Section from 'design-system/Section';
import Text from 'design-system/Text';
import GroupAvatar from 'features/group/components/GroupAvatar';
import { useAddGroupModal } from 'features/group/hooks/useAddGroupModal';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { formatPlural } from 'utils';
import Banner from 'design-system/Banner';
import IconThreeUser from 'design-system/icons/IconThreeUser';
import IconPlus from 'design-system/icons/IconPlus';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import { IconFatPlus } from 'design-system/icons';
import { selectGroups } from 'reducers/selectors';
import { WalkthroughContext } from 'features/feed/components/walkthrough/context';
const GroupsSection = ({ cardViewStyle, titleContainerStyle, maxGroups, hideAddButton, button, }) => {
    const navigation = useMainStackNavigation();
    const groupsData = useAppSelector(selectGroups);
    const { show } = useAddGroupModal();
    const colors = useColors();
    const styles = useStyles(styleSet);
    const isInWalkthrough = useContext(WalkthroughContext);
    const groups = useMemo(() => {
        if (isInWalkthrough && maxGroups === undefined) {
            return groupsData?.slice(0, 2);
        }
        return maxGroups !== undefined
            ? groupsData?.slice(0, maxGroups)
            : groupsData;
    }, [groupsData, isInWalkthrough, maxGroups]);
    if (!groups.length) {
        return (<Section title="Groups" style={[styles.section, cardViewStyle]} titleContainerStyle={titleContainerStyle}>
        <Banner noMarginBottom noMargin={isInWalkthrough} onPress={show} title="Welcome to Groups" buttonTitle="Create or join a group" icon={<IconThreeUser gradientKey="orange" alt/>} buttonLeftIcon={<IconFatPlus width={16} height={16} color={colors.text.textOnBrand}/>} description="Trying to work out who owes what for lunch, drinks or household bills? Create a group in Emma to track, split and settle shared expenses."/>
      </Section>);
    }
    return (<Section title="Groups" button={button} style={[styles.section, cardViewStyle]} titleContainerStyle={titleContainerStyle}>
      <CardView noMarginTop noMarginBottom noMargin={isInWalkthrough}>
        {!hideAddButton && (<SettingsItem onPress={show} title="Add new group" renderIcon={() => (<View style={styles.addNewGroup}>
                <IconPlus color={colors.text.brand}/>
              </View>)}/>)}
        {groups.map((group) => (<TouchableHighlight onPress={() => navigation.navigateDeprecated('Group', { id: group.id })} underlayColor={colors.background.underlay} key={group.id}>
            <View style={styles.groupRow}>
              <GroupAvatar name={group.name} url={group.iconUrl}/>
              <View style={styles.groupRowLeftContent}>
                <Text Text-16 OffBlack numberOfLines={1}>
                  {group.name}
                </Text>

                <Text TextThin-14 Secondary>
                  {group.userIds.length}{' '}
                  {formatPlural('member', 'members', group.userIds?.length || 0)}
                </Text>
              </View>
              <View style={styles.groupRowRightContentText}>
                <Amount Numbers-16 right value={{
                value: Math.abs(group.netBalance.balance),
                currency: group.netBalance.currency,
            }}/>
                <Text TextThin-14 Secondary right>
                  {group.netBalance.balance < 0 ? 'you owe' : 'owed'}
                </Text>
              </View>
            </View>
          </TouchableHighlight>))}
      </CardView>
    </Section>);
};
const styleSet = createStyleSheets((colors) => ({
    section: {
        marginTop: rem(8),
    },
    groupRow: {
        padding: rem(16),
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    groupRowLeftContent: {
        flex: 1,
        marginLeft: rem(16),
        justifyContent: 'center',
    },
    groupRowRightContentText: {
        marginLeft: rem(16),
        justifyContent: 'flex-end',
        textAlign: 'right',
    },
    addNewGroup: {
        width: rem(44),
        height: rem(44),
        borderRadius: rem(50),
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: colors.background.dark,
        marginRight: rem(16),
    },
}));
export default memo(GroupsSection);
